// Селектор активного заведения для админ-панели.
// Показывает список из /api/admin/establishments, текущий выбор хранится в
// useEstablishmentStore и автоматически попадает во все запросы через
// X-Establishment-Id. При переключении инвалидируем весь react-query кеш,
// чтобы отчёты, меню, склад и т.д. перезагрузились под новое заведение.
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Store } from 'lucide-react';

import { listEstablishments } from '@/services/admin';
import { useEstablishmentStore } from '@/store/establishment.store';
import { useAuthStore } from '@/store/auth.store';
import { invalidateEstablishmentScoped } from '@/utils/invalidate';

export function EstablishmentSwitcher() {
  const user = useAuthStore((s) => s.user);
  const currentId = useEstablishmentStore((s) => s.currentId);
  const setCurrentId = useEstablishmentStore((s) => s.setCurrentId);
  const qc = useQueryClient();

  const canSwitch = !!user && (user.role === 'ADMIN' || user.role === 'OWNER');

  const { data: ests = [] } = useQuery({
    queryKey: ['admin/ests'],
    queryFn: listEstablishments,
    enabled: canSwitch,
  });

  // Первичная установка текущего заведения: берём сохранённое, иначе — из JWT,
  // иначе — первое из списка. Если сохранённого больше нет в списке (удалили) —
  // откатываемся к первому доступному.
  useEffect(() => {
    if (!canSwitch || ests.length === 0) return;
    const ids = new Set(ests.map((e) => e.id));
    if (!currentId || !ids.has(currentId)) {
      const fallback = user?.establishmentId && ids.has(user.establishmentId)
        ? user.establishmentId
        : ests[0].id;
      setCurrentId(fallback);
    }
  }, [ests, currentId, canSwitch, user?.establishmentId, setCurrentId]);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!canSwitch) return null;

  const current = ests.find((e) => e.id === currentId) ?? null;

  // Если у пользователя только одно заведение (большинство клиентов) —
  // селектор не нужен. Показываем название статично, без выпадающего списка.
  const hasMultiple = ests.length > 1;

  const onPick = (id: string) => {
    if (id === currentId) {
      setOpen(false);
      return;
    }
    setCurrentId(id);
    setOpen(false);
    invalidateEstablishmentScoped(qc);
  };

  if (!hasMultiple) {
    const name = current?.name ?? ests[0]?.name ?? 'Заведение';
    return (
      <div className="px-3 py-3 border-b border-slate-100">
        <div className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-50">
          <div className="p-1.5 rounded-md bg-white text-brand-600 border border-slate-200">
            <Store size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Заведение</div>
            <div className="text-sm font-medium truncate">{name}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-3 py-3 border-b border-slate-100" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="p-1.5 rounded-md bg-white text-brand-600 border border-slate-200">
          <Store size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Заведение</div>
          <div className="text-sm font-medium truncate">
            {current?.name ?? 'Выберите…'}
          </div>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 z-30 bg-white rounded-lg shadow-lg border border-slate-100 overflow-hidden">
          <div className="max-h-64 overflow-auto py-1">
            {ests.map((e) => {
              const active = e.id === currentId;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => onPick(e.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                    active ? 'bg-brand-50/60 text-brand-700' : 'text-slate-700'
                  }`}
                >
                  <span className="flex-1 min-w-0 truncate">{e.name}</span>
                  {active && <Check size={14} className="text-brand-600" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
